// One flat view of GET /assistants/:id/knowledge-status.
//
// The endpoint drives three screens - learning, ready, failed - and the same
// payload is read by the onboarding step and by Install & Settings, so it is
// interpreted once here rather than in each of them.

// The stages the crawl moves through, in order, as the API names them. The
// strip under the progress bar is these five.
export const STAGES = [
  { key: "discovering", label: "Finding pages" },
  { key: "reading", label: "Reading" },
  { key: "learning", label: "Learning" },
  { key: "understanding", label: "Understanding" },
  { key: "checking", label: "Checking" },
];

const num = (value) => (typeof value === "number" ? value : null);

/**
 * @param {object} body the `data` object from the endpoint
 * @returns {object} flat fields, with nulls where the API sent none
 */
export const readKnowledgeStatus = (body) => {
  const progress = body?.progress || {};
  const stage = progress.stage || "";

  const pagesRead = num(progress.pagesRead);
  const pageLimit = num(progress.pageLimit);

  // Only meaningful while pages are being read: the other stages have no
  // count behind them, and a bar that sits at 100% through three of them
  // says less than no bar at all.
  const percent =
    stage === "reading" && pagesRead !== null && pageLimit
      ? Math.min(100, Math.round((pagesRead / pageLimit) * 100))
      : null;

  return {
    website: body?.website || "",
    status: body?.status || "",
    // The one flag that decides whether to keep polling.
    isLearning: Boolean(body?.isLearning),
    stage,
    message: progress.message || "",
    pagesFound: num(progress.pagesFound),
    pagesRead,
    pageLimit,
    percent,
    pagesLearned: num(body?.pagesLearned),
    lastSyncedAt: body?.lastSyncedAt || null,
    // Present on a failure, and on a re-crawl that failed while the assistant
    // itself carries on answering from what it already knows.
    lastError: body?.lastError || null,
    // Both null until the first crawl with these features has finished.
    check: body?.check || null,
    business: body?.business || null,
  };
};

/**
 * Which of the three screens this payload is: `isLearning` wins, then a
 * failure, then it is ready.
 */
export const knowledgeScreen = (state) => {
  if (!state) return "loading";
  if (state.isLearning) return "learning";
  if (state.status === "failed") return "failed";
  return "ready";
};

/** How far through the five stages, for the strip. -1 before it starts. */
export const stageIndex = (stage) =>
  STAGES.findIndex((item) => item.key === stage);

/**
 * The same flat view, read out of an assistant record instead.
 *
 * GET /assistants/:id carries the crawl's state under `crawlProgress`, with
 * `lastCrawledAt` and `lastCrawlError` beside it. That record is loaded by
 * every page that shows an assistant, so reading it is what lets a crawl's
 * figures survive a refresh without a second request - knowledge-status is
 * for watching a crawl that is running, not for remembering one that ran.
 *
 * @param assistant the record from GET /assistants/:id
 * @returns the readKnowledgeStatus shape, or null when the site has never
 *   been crawled and there is nothing to report
 */
export const readCrawlProgress = (assistant) => {
  const progress = assistant?.crawlProgress || null;

  if (!progress && !assistant?.lastCrawledAt && !assistant?.lastCrawlError) {
    return null;
  }

  const stage = progress?.stage || "";
  const pagesRead = num(progress?.pagesRead);
  const pageLimit = num(progress?.pageLimit);

  // A stage that is one of the five is a crawl still working; "ready" and
  // "failed" are the two it finishes on.
  const isLearning = STAGES.some((item) => item.key === stage);

  // An error from a crawl that has since succeeded is history, not news, so
  // it is only carried while the latest stage isn't a finished one.
  const failed = stage === "failed" || (Boolean(assistant?.lastCrawlError) && stage !== "ready");

  return {
    website: assistant?.sourceUrl || "",
    status: failed ? "failed" : stage === "ready" ? "ready" : stage,
    isLearning,
    stage,
    message: progress?.message || "",
    pagesFound: num(progress?.pagesFound),
    pagesRead,
    pageLimit,
    percent:
      stage === "reading" && pagesRead !== null && pageLimit
        ? Math.min(100, Math.round((pagesRead / pageLimit) * 100))
        : null,
    // The record counts pages read; what came of them is knowledge-status's
    // to say, so this is left unclaimed rather than guessed at.
    pagesLearned: null,
    lastSyncedAt: assistant?.lastCrawledAt || null,
    lastError: failed ? assistant?.lastCrawlError || null : null,
    check: assistant?.knowledgeReport?.check || null,
    business: assistant?.businessProfile || null,
  };
};
